import { Button } from "@/components/ui/button";
import { VerificationEvidencePreview } from "../verification-evidence-preview/verification-evidence-preview";
import { VerificationSourceLink } from "../verification-source-link";
import { useOpenVerificationEvidence } from "../license-verification/use-open-verification-evidence";
import { CHECK_STATUS_LABELS } from "../license-verification/types";
import type { RunReport, RunScope } from "./types";

const RESULT_LABELS = {
  "record-found": "Record found",
  "no-record-found": "No record found",
  "no-matches": "No matching records",
  "possible-match": "Possible match — review needed",
  "adverse-record": "Adverse finding — review needed",
  incomplete: "Incomplete result",
  "not-applicable": "Not applicable",
};

export function OtherCheckResults({
  check,
  runId,
  scope,
}: {
  check: RunReport["checks"][number];
  runId: string;
  scope: RunScope;
}) {
  const evidence = useOpenVerificationEvidence({
    scope,
    runId,
    checkId: check.id,
  });
  return (
    <section className="space-y-3" aria-label={check.authorityName}>
      <h3 className="text-sm font-semibold">{check.authorityName}</h3>
      <p className="text-sm text-muted-foreground">
        {CHECK_STATUS_LABELS[check.status]}
      </p>
      {(check.results ?? []).map((result) => (
        <div key={result.resultKey} className="space-y-2 border-t pt-3">
          <p className="text-sm">{RESULT_LABELS[result.outcome]}</p>
          <VerificationSourceLink sourceUrl={result.facts.sourceUrl} />
        </div>
      ))}
      {!check.results?.length && (
        <p className="text-sm text-muted-foreground">
          No source findings are available for this check.
        </p>
      )}
      {check.evidenceManifest.length > 0 && (
        <details className="space-y-2 border-t pt-3">
          <summary className="cursor-pointer text-sm">
            Supporting evidence
          </summary>
          <div className="flex flex-wrap gap-2">
            {check.evidenceManifest.map((file, index) => (
              <Button
                key={file.fileUploadId}
                variant="surface"
                size="sm"
                onClick={() =>
                  evidence.open({
                    fileUploadId: file.fileUploadId,
                    label: `Source evidence ${index + 1}`,
                  })
                }
              >
                Source evidence {index + 1}
              </Button>
            ))}
          </div>
        </details>
      )}
      <VerificationEvidencePreview {...evidence} />
    </section>
  );
}
